import { createApp, onBeforeMount, ref, onUnmounted, onMounted } from 'vue';
import ShokzMember from '@theme/shokz-member';

/**
 * @import {SimpleToast} from '@theme/component-simple-toast'
 */

/** @type {SimpleToast} */
const simpleToast = document.getElementById('simple-toast');

function pushWalletSelectContent(buttonName) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event_parameters: null });
  window.dataLayer.push({
    event: 'ga4Event',
    event_name: 'select_content',
    event_parameters: {
      content_type: 'section',
      content_name: 'hub_member_summary_section',
      button_name: buttonName,
    },
  });
}

/**
 * 判断当前设备是否符合 "Add to Apple Wallet" 的要求
 * @returns {boolean} true 表示支持，false 表示不支持
 */
function isAddToAppleWalletSupported() {
  const ua = navigator.userAgent;
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua);
  const isiOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isMac = /Macintosh/.test(ua);

  // iOS 设备支持情况
  if (isiOS) {
    // Safari 始终支持 (iOS 10+)
    if (isSafari) return true;

    // iOS 16+ 的第三方浏览器 (Chrome, Firefox, Edge) 支持[citation:2]
    const iOSVersionMatch = ua.match(/OS ([\d_]+)/);
    if (iOSVersionMatch) {
      const iOSVersion = parseInt(iOSVersionMatch[1].replace(/_/g, '.'), 10);
      if (iOSVersion >= 16) return true;
    }
  }

  // macOS 设备: 仅 Safari 16+ 支持 (对应 macOS Monterey 12.3+)
  if (isMac && isSafari) {
    const macOSMatch = ua.match(/Mac OS X ([\d_]+)/);
    if (macOSMatch) {
      const macOSVersion = parseFloat(macOSMatch[1].replace(/_/g, '.'));
      return macOSVersion >= 12;
    } else {
      return false;
    }
  }

  return false;
}

/**
 * 创建 HubWalletCard 的 Vue 实例
 * @param {HubWalletCard} section
 * @returns
 */
function createHubWalletCardVueEntry(section) {
  const app = createApp({
    delimiters: ['${', '}'],
    setup() {
      const loading = ref(true);
      const currentTierLevel = ref(1);
      const appleWalletLink = ref('');
      const googleWalletLink = ref('');
      const isTierBannerVisible = (tierLevel) => Number(tierLevel) === Number(currentTierLevel.value);

      /**
       * 会员模块初始化后的回调，映射组件渲染数据
       * @param {{ memberData: import("@theme/shokz-member-type").MemberData, walletLink: import("@theme/shokz-member-type").WalletLink[] }} data
       */
      const render = (data) => {
        const { memberData, walletLink } = data || {};
        if (memberData == undefined || walletLink == undefined) {
          console.error('[hub-wallet-card]: memberData or walletLink is undefined');
          return;
        }

        currentTierLevel.value = Number(ShokzMember.getTierNumber(memberData)) || 1;
        console.log('[hub-wallet-card]: walletLink', walletLink);
        appleWalletLink.value = walletLink?.[0]?._actions?.download_apple?.url
          ? ShokzMember.antavoBaseUrl + walletLink[0]._actions.download_apple.url
          : '';
        googleWalletLink.value = walletLink?.[0]?._actions?.download_google?.url
          ? ShokzMember.antavoBaseUrl + walletLink[0]._actions.download_google.url
          : '';
      };


      /**
       * 会员信息初始化后的订阅者
       * @type {Function}
       */
      let memberInfoInitializedUnsubscriber;
      const onClickAppleWallet = (e) => {
        if (!appleWalletLink.value || loading.value) return;
        if (!isAddToAppleWalletSupported()) {
          // 不符合要求的设备弹出提示
          simpleToast.show('Please using the latest Safari browser on your iOS or macOS device.', 5000);
          e.preventDefault();
        }
        pushWalletSelectContent('Add_To_Apple_Wallet');
      };
      const onClickGoogleWallet = () => {
        if (!googleWalletLink.value || loading.value) return;
        pushWalletSelectContent('Add_To_Google_Wallet');
      };
      onBeforeMount(() => {
        memberInfoInitializedUnsubscriber = subscribe(PUB_SUB_EVENTS.memberInfoInitialized, onMemberInfoInitialized);
      });
      onMounted(() => {
        section.querySelector('[data-apple-wallet-link]')?.addEventListener('click', onClickAppleWallet);
        section.querySelector('[data-google-wallet-link]')?.addEventListener('click', onClickGoogleWallet);
        if (ShokzMember.memberData != undefined) {
          reload();
        }
      });
      onUnmounted(() => {
        section.querySelector('[data-apple-wallet-link]')?.removeEventListener('click', onClickAppleWallet);
        section.querySelector('[data-google-wallet-link]')?.removeEventListener('click', onClickGoogleWallet);
        if (memberInfoInitializedUnsubscriber) {
          memberInfoInitializedUnsubscriber();
        }
      });

      // ============ 方法 ============
      /**
       * 会员信息初始化后加载模块
       * @param {{data: import("./shokz-member-type").MemberData}} data 
       */
      const onMemberInfoInitialized = (data) => {
        reload();
      }

      const reload = () => {
        loading.value = true;
        fetchData().then(data => {
          console.log('[hub-wallet-card]: fetchData', data);
          render(data);
        }).catch(error => {
          simpleToast.show(error?.message || 'An unknown error occurred, please try again later.');
          console.error('[hub-wallet-card]: fetchData error', error);
        }).finally(() => {
          loading.value = false;
        });
      }

      const fetchData = async () => {
        const data = await Promise.all([
          ShokzMember.getMemberDataByOnceCall(),
          ShokzMember.getWalletLink(),
        ]);
        return {
          memberData: data[0],
          walletLink: data[1].data.data,
        };;
      }



      return {
        loading,
        appleWalletLink,
        googleWalletLink,
        isTierBannerVisible,
      };
    }
  });

  app.mount(section.querySelector('[data-vue-render]'));
  return app;
}

class HubWalletCard extends HTMLElement {
  app = undefined;

  connectedCallback() {
    this.app = createHubWalletCardVueEntry(this);
  }

  disconnectedCallback() {
    if (this.app) this.app.unmount();
  }
}

if (!customElements.get('hub-wallet-card')) {
  customElements.define('hub-wallet-card', HubWalletCard);
}